import { ILineItem } from '../types/line-item.types';
import { IOverrideState } from '../types/verify-travel.types';
import { IWatrLimit } from '@nkr/models/index';
import { ExpenseType } from '../constants/expense-types';
import { calculateExpenseTotals } from './expense.utils';

const calculateLodgingApprovedAmount = (lineItems: ILineItem[], requestedAmount: number, maxNights: number): number => {
    const lodgingLineItems = lineItems.filter(li => li.expenseType === ExpenseType.LODGING);
    const totalNights = lodgingLineItems.reduce((sum, li) => sum + (li.numberOfNights || 0), 0);
    
    if (totalNights > maxNights && totalNights > 0) {
        return Math.round((requestedAmount / totalNights) * maxNights * 100) / 100;
    }
    
    return requestedAmount;
};

export const calculateApprovedLodgingAmount = (
    lineItems: ILineItem[],
    limit: IWatrLimit,
    overrideState: IOverrideState,
    viewType: 'trip' | 'overall'
): number => {
    if (viewType === 'overall' && overrideState.isHotelLodgingExpenseOverridden) {
        const overrideValue = overrideState.overrideHotelLodgingExpense;
        if (overrideValue !== undefined && overrideValue !== null && overrideValue >= 0) {
            return Math.ceil(overrideValue);
        }
    }
    
    const requested = lineItems.filter(li => li.expenseType === ExpenseType.LODGING).reduce((sum, li) => sum + li.amount, 0);
    const approved = calculateLodgingApprovedAmount(lineItems, requested, limit.hotelNights);
    return Math.ceil(approved);
};

export const calculateApprovedTransportationAmount = (
    lineItems: ILineItem[],
    limit: IWatrLimit,
    overrideState: IOverrideState,
    viewType: 'trip' | 'overall',
    totalCarServiceExpenseReimbursementAmountWithOverride: number
): number => {
    if (viewType === 'overall' && overrideState.isCarServiceExpenseOverridden) {
        const overrideValue = overrideState.overrideCarServiceExpense;
        if (overrideValue !== undefined && overrideValue !== null && overrideValue >= 0) {
            return Math.ceil(overrideValue);
        }
    }
    
    let amount = totalCarServiceExpenseReimbursementAmountWithOverride;
    if (!amount || amount === 0) {
        const transportationLineItems = lineItems.filter(li => li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE);
        amount = transportationLineItems.reduce((sum, li) => sum + li.amount, 0);
    }
    const limitValue = limit.transportationExpenses;
    return Math.ceil(Math.min(amount, limitValue));
};

export const calculateApprovedMealsAmount = (
    limit: IWatrLimit,
    overrideState: IOverrideState,
    viewType: 'trip' | 'overall',
    totalMealsReimbursementAmountWithOverride: number
): number => {
    if (viewType === 'overall' && overrideState.isMealsOverWritten) {
        const overrideValue = overrideState.overrideMeals;
        if (overrideValue !== undefined && overrideValue !== null && overrideValue >= 0) {
            return Math.ceil(overrideValue);
        }
    }
    
    const amount = totalMealsReimbursementAmountWithOverride;
    const limitValue = limit.meals;
    return Math.ceil(Math.min(amount, limitValue));
};

export const calculateApprovedDependentCareAmount = (
    limit: IWatrLimit,
    overrideState: IOverrideState,
    viewType: 'trip' | 'overall',
    totalDependentCarExpenseReimbursementAmountWithOverride: number
): number => {
    if (viewType === 'overall' && overrideState.isDependentCareExpenseOverridden) {
        const overrideValue = overrideState.overrideDependentCareExpense;
        if (overrideValue !== undefined && overrideValue !== null && overrideValue >= 0) {
            return Math.ceil(overrideValue);
        }
    }
    
    const amount = totalDependentCarExpenseReimbursementAmountWithOverride;
    const limitValue = limit.dependentCare;
    return Math.ceil(Math.min(amount, limitValue));
};

export const calculateApprovedMileageAmount = (
    limit: IWatrLimit,
    overrideState: IOverrideState,
    viewType: 'trip' | 'overall',
    totalMilesReimbursementAmountWithOverride: number,
    reimbursementRate: number
): number => {
    const mileageLimit = limit.mileage * reimbursementRate;
    
    if (viewType === 'overall' && overrideState.isMilesOverWritten && overrideState.overrideMiles > 0) {
        return Math.ceil(totalMilesReimbursementAmountWithOverride);
    }
    
    return Math.ceil(Math.min(totalMilesReimbursementAmountWithOverride, mileageLimit));
};

export const calculateApprovedFlightAmount = (
    lineItems: ILineItem[]
): number => {
    const requested = lineItems.filter(li => li.expenseType === ExpenseType.FLIGHT).reduce((sum, li) => sum + li.amount, 0);
    return Math.ceil(requested);
};

export const calculateRawRequestedAmounts = (allLineItems: ILineItem[], expenseTotals: ReturnType<typeof calculateExpenseTotals>) => {
    return {
        transportation: allLineItems.filter(li => li.expenseType === ExpenseType.TRANSPORTATION || li.expenseType === ExpenseType.CAR_SERVICE).reduce((sum, li) => sum + li.amount, 0),
        meals: expenseTotals.meals,
        dependentCare: expenseTotals.dependentCare
    };
};

export const calculateTotalCarServiceExpenseReimbursementAmountWithOverride = (
    overrideState: IOverrideState,
    rawRequestedAmounts: ReturnType<typeof calculateRawRequestedAmounts>,
    expenseTotals: ReturnType<typeof calculateExpenseTotals>
): number => {
    if (overrideState.isCarServiceExpenseOverridden) {
        return overrideState.overrideCarServiceExpense > 0 
            ? overrideState.overrideCarServiceExpense 
            : (rawRequestedAmounts.transportation > 0 ? rawRequestedAmounts.transportation : expenseTotals.transportation);
    }
    return expenseTotals.transportation;
};

export const calculateTotalMealsReimbursementAmountWithOverride = (
    overrideState: IOverrideState,
    rawRequestedAmounts: ReturnType<typeof calculateRawRequestedAmounts>,
    expenseTotals: ReturnType<typeof calculateExpenseTotals>
): number => {
    if (overrideState.isMealsOverWritten) {
        return overrideState.overrideMeals > 0 
            ? overrideState.overrideMeals 
            : (rawRequestedAmounts.meals > 0 ? rawRequestedAmounts.meals : expenseTotals.meals);
    }
    return expenseTotals.meals;
};

export const calculateTotalDependentCarExpenseReimbursementAmountWithOverride = (
    overrideState: IOverrideState,
    rawRequestedAmounts: ReturnType<typeof calculateRawRequestedAmounts>,
    expenseTotals: ReturnType<typeof calculateExpenseTotals>
): number => {
    if (overrideState.isDependentCareExpenseOverridden) {
        return overrideState.overrideDependentCareExpense > 0 
            ? overrideState.overrideDependentCareExpense 
            : (rawRequestedAmounts.dependentCare > 0 ? rawRequestedAmounts.dependentCare : expenseTotals.dependentCare);
    }
    return expenseTotals.dependentCare;
};

export const calculateTotalMilesReimbursementAmountWithOverride = (
    overrideState: IOverrideState,
    totalMileageReimbursement: number,
    reimbursementRate: number
): number => {
    if (overrideState.isMilesOverWritten && overrideState.overrideMiles > 0) {
        return overrideState.overrideMiles * reimbursementRate;
    }
    return totalMileageReimbursement;
};

export const calculateRoundTripMiles = (
    overrideState: IOverrideState,
    totalMiles: number,
    mileageLimit: number
): number => {
    if (overrideState.isMilesOverWritten && overrideState.overrideMiles > 0) {
        return overrideState.overrideMiles;
    }
    return Math.min(totalMiles, mileageLimit);
};
